
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { generateConnectionCode } from '@/services/telegram/connectionUtils';
import { isSystemBotTokenConfigured, getSystemBotInfo } from '@/services/telegram/botTokenService';

interface TelegramConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BotInfo {
  first_name: string;
  username: string;
  id: number;
}

export function TelegramConnectDialog({ open, onOpenChange }: TelegramConnectDialogProps) {
  const [connectionCode, setConnectionCode] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
  const [isBotConfigured, setIsBotConfigured] = useState<boolean>(false);
  const [isCheckingBot, setIsCheckingBot] = useState<boolean>(true);
  const { toast } = useToast();
  const { user } = useAuth();

  // Check bot configuration and get bot info when dialog opens
  useEffect(() => {
    if (open) {
      const checkBotConfiguration = async () => {
        setIsCheckingBot(true);
        try {
          const configured = await isSystemBotTokenConfigured();
          setIsBotConfigured(configured);
          
          if (configured) {
            const info = await getSystemBotInfo();
            setBotInfo(info);
          }
        } catch (error) {
          console.error('Error checking bot configuration:', error);
          setIsBotConfigured(false);
          setBotInfo(null);
        } finally {
          setIsCheckingBot(false);
        }
      };
      
      checkBotConfiguration();
    }
  }, [open]);

  const generateCode = async () => {
    if (!user?.id) return;
    
    setIsGenerating(true);
    try {
      const result = await generateConnectionCode(user.id);
      
      if (result) {
        setConnectionCode(result.code);
        setExpiresAt(result.expires_at);
        toast({
          title: "Connection code generated",
          description: "Use this code with the /start command in Telegram",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate connection code",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error generating code:', error);
      toast({
        title: "Error",
        description: "Failed to generate connection code",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(connectionCode);
    toast({
      title: "Copied!",
      description: "Connection code copied to clipboard",
    });
  };

  const openTelegram = () => {
    if (!botInfo?.username) {
      toast({
        title: "Bot not available",
        description: "Bot information not available",
        variant: "destructive"
      });
      return;
    }
    
    const url = `https://t.me/${botInfo.username}?start=${connectionCode}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (open && user?.id && isBotConfigured && !isCheckingBot) {
      generateCode();
    }
  }, [open, user?.id, isBotConfigured, isCheckingBot]);

  if (isCheckingBot) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Checking Bot Configuration...</DialogTitle>
            <DialogDescription>
              Please wait while we verify the bot setup
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center p-4">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!isBotConfigured) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bot Not Configured</DialogTitle>
            <DialogDescription>
              The system Telegram bot is not configured
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              The Telegram bot token has not been configured in the system. Please contact your administrator to set up the bot integration.
            </AlertDescription>
          </Alert>
          
          <div className="flex justify-end">
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connect Your Telegram Account</DialogTitle>
          <DialogDescription>
            {botInfo ? 
              `Connect to ${botInfo.first_name} (@${botInfo.username}) to receive notifications` :
              'Follow these steps to connect your Telegram account and receive notifications'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {botInfo && (
            <div className="p-3 bg-muted rounded-md">
              <h4 className="font-medium">Bot Information:</h4>
              <div className="text-sm mt-1">
                <p><strong>Name:</strong> {botInfo.first_name}</p>
                <p><strong>Username:</strong> @{botInfo.username}</p>
              </div>
            </div>
          )}
          
          <Alert>
            <AlertDescription>
              <strong>Step 1:</strong> Start a conversation with the bot above
            </AlertDescription>
          </Alert>
          
          <Alert>
            <AlertDescription>
              <strong>Step 2:</strong> Send the following command to the bot:
            </AlertDescription>
          </Alert>
          
          {connectionCode ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="connection-code">Connection Command</Label>
                <div className="flex space-x-2">
                  <Input
                    id="connection-code"
                    value={`/start ${connectionCode}`}
                    readOnly
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copyCode}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                Code expires: {new Date(expiresAt).toLocaleString()}
              </div>
              
              <div className="flex space-x-2">
                <Button
                  onClick={openTelegram}
                  className="flex-1"
                  disabled={!botInfo?.username}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open {botInfo?.first_name || 'Telegram'}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={generateCode}
                  disabled={isGenerating}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                  New Code
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <Button
                onClick={generateCode}
                disabled={isGenerating}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                Generate Connection Code
              </Button>
            </div>
          )}
          
          <Alert>
            <AlertDescription>
              <strong>Step 3:</strong> Once connected, you'll receive a confirmation message and start getting procurement notifications.
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
}
